import CtaBtn from "@/components/ui/cta-btn";
import Link from "next/link";
import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { productEnquiryFormFields } from "@/helper/forms/product-enquiry-form";
import { checkFormValidity, validateInput } from "@/utils/validate";
import Select from "@/components/ui/form/form-field/select";
import TextBox from "@/components/ui/form/form-field/textbox";
import TextArea from "@/components/ui/form/form-field/textarea";
import FormBtn from "@/components/ui/form-btn";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { trackLeadFormSubmission } from "@/utils/trackFormEvent";

const BlogEnquiry = ({ product_code, type, clicked, handleCloseBlogPopup}) => {


  const [formElement, setFormElements] = useState(productEnquiryFormFields);
  const [formSubmitErrorMessage, setFormSubmitErrorMessage] = useState("");
  const [isFormValid, setFormValid] = useState(false);
  const [formSubmitLoader, setFormSubmitLoader] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtpPopup, setShowOtpPopup] = useState(false);

  const [showMobilePopup, setShowMobilePopup] = useState(clicked);

  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(600); // 10 minutes in seconds
  const [otpError, setOtpError] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("");
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [downloadOrView, setDownloadOrView] = useState(null);
  const [sendOtpLoader, setSendOtpLoader] = useState(false);
  const [otpVerifyLoader, setOtpVerifyLoader] = useState(false);
  const [isOtpVerify, setisOtpVerify] = useState(false);


  const timerRef = useRef(null);
  // Handle input change and update form state
  const inputChangedHandler = (event) => {
    const name = event.target.name;
    let value = event.target.value;
    if (name === "name") {
      value = value.replace(/[^a-zA-z\s]/g, "");
    }

    const updatedForm = { ...formElement };
    const updatedFormElement = { ...updatedForm[name] };

    updatedFormElement.value = value;
    updatedForm[name] = validateInput(updatedFormElement);
    setFormElements(updatedForm);

    // Show/hide Send OTP button based on valid mobile number
    if (name === "contact") {
      const contactValid = updatedForm.contact.isValid;
      if (contactValid) {
        setOtpSent(true);
        setMobileNumber(value);
      } else {
        setOtpSent(false);
        setMobileNumber("");
      }
    }
  };

  // Handler for form input blur events
  const inputBlurHandler = (event) => {
    const name = event.target.name;

    const updatedForm = { ...formElement };
    const updatedFormElement = { ...updatedForm[name] };

    updatedFormElement.isTouched = true;
    updatedForm[name] = updatedFormElement;
    setFormElements(updatedForm);
  };

  // Send OTP to the user's mobile number
  const handleSendOtp = async () => {
    setSendOtpLoader(true); // Start sendOtpLoader

    try {
      // API call to send OTP
      const response = await fetch("/api/otp/handler", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          formName: "product-enquiry",
          action: "sendOtp",
          contactNumber: mobileNumber,
        }),
      });

      if (response.ok) {
        toast.success("OTP on mobile number sent successfully", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setOtpSent(true);
        setShowMobilePopup(false);
        setShowOtpPopup(true);
        setTimer(600); // Reset timer to 10 minutes
        startTimer(); // Start the timer
      } else {
        setPopupMessage("Failed to send OTP");
        setPopupType("error");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setPopupMessage("An error occurred while sending OTP.");
      setPopupType("error");
    } finally {
      setSendOtpLoader(false); // Stop sendOtpLoader
    }
  };

  // Verify the entered OTP
  const handleVerifyOtp = async () => {
    if (!otp) {
      setOtpError("OTP is required");
      return;
    }
    setOtpVerifyLoader(true);

    try {
      // API call to verify OTP
      const response = await fetch("/api/otp/handler", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          formName: "product-enquiry",
          action: "verifyOtp",
          contactNumber: mobileNumber,
          otp: otp,
        }),
      });

      const verifyOtpResponseData = await response.json();
      if (response.ok) {
        setPopupMessage("OTP verified successfully");
        toast.success("Mobile number verified successfully", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setPopupType("success");
        setShowOtpPopup(false);
        stopTimer(); // Stop the timer on successful OTP verification
        setShowMobilePopup(true);
        setOtpVerified(true);
        setOtpSent(false);
      } else {
        setPopupMessage(verifyOtpResponseData?.data?.text);
        setPopupType("error");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setPopupMessage("Failed to verify OTP. Please try again.");
      setOtpError("An error occurred while verifying OTP.");
      setPopupType("error");
    } finally {
      setOtpVerifyLoader(false); // Stop sendOtpLoader
    }
  };

  // Handle form submission
  const formSubmitHandler = async (event) => {
    event.preventDefault();

    const updatedForm = { ...formElement };
    // Validate all form fields
    for (let element in updatedForm) {
      const updatedFormElement = { ...updatedForm[element] };
      updatedForm[element] = validateInput(updatedFormElement);
    }

    const isFormValid = checkFormValidity(updatedForm);
    if (isFormValid) {
      if (!otpVerified) {
        alert("Please verify the OTP before submitting the form.");
        return;
      }
      setFormSubmitLoader(true);

      // Capture UTM parameters from the URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source') || '';
      const utmMedium = urlParams.get('utm_medium') || '';
      const utmCampaign = urlParams.get('utm_campaign') || '';
      const utmContent = urlParams.get('utm_content') || '';

      // Prepare form data for submission
      const formDataToSend = new FormData();
      formDataToSend.append("name", updatedForm.name.value);
      formDataToSend.append("email", updatedForm.email.value);
      formDataToSend.append("contact_number", updatedForm.contact.value);
      formDataToSend.append("pincode", updatedForm.pincode.value);
      formDataToSend.append("country_code", "+91");
      formDataToSend.append("form_name", "product-enquiry");
      formDataToSend.append("product_code", product_code);
      formDataToSend.append("type", type);

      // Append UTM parameters to the form data
      formDataToSend.append('utm_source', utmSource);
      formDataToSend.append('utm_medium', utmMedium);
      formDataToSend.append('utm_campaign', utmCampaign);
      formDataToSend.append('utm_content', utmContent);

      formDataToSend.append('endpoint', 'form/product-enquiry/store');

      try {
        const response = await fetch("/api/form/handler", {
          method: "POST",
          body: formDataToSend,
        });

        const responseData = await response.json();

        if (response.ok) {

          toast.success("Your Enquiry has been sent successfully", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          trackLeadFormSubmission('product-detail-enquiry')
          setShowMobilePopup(false);
          setFormElements(productEnquiryFormFields);
          // Allow file download or view

          // await checkVerificationRequirementWithVerification(selectedItemId);
          setFormSubmitLoader(false);
          setFormSubmitErrorMessage("");
        } else {
          setFormSubmitErrorMessage(responseData.message);
        }
      } catch (error) {
        setFormSubmitErrorMessage(
          "An error occurred while submitting the form."
        );
        console.error("Error submitting form:", error);
      } finally {
        // Reset state after submission
        setFormValid(false);
        // setFormElements(productEnquiryFormFields);
        // setOtpSent(false);
        // setOtpVerified(false);
        setOtp("");
        setFormSubmitLoader(false);
      }
    } else {
      setFormValid(false);
      setFormElements(updatedForm);
    }
  };


  // Clean up the timer when the component unmounts
  useEffect(() => {
    return () => stopTimer();
  }, []);

  // Function to start the OTP expiration timer
  const startTimer = () => {
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 0) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  // Stop the timer when OTP is verified successfully
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleEnquiryClick = (e) => {
    e.preventDefault(); // Prevents page reload
    setShowMobilePopup(true);
  };


  useEffect(() => {
      if(clicked){
        setShowMobilePopup(false);
      }else{
        setShowMobilePopup(true);
      }
  }, []);


  // Format time in MM:SS format
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Handle Enter key press event to trigger OTP verification
  const handleKeyDown = (event, inputBoxAction) => {
    const fieldName = event.target.name;

    // Only apply to the name field
    if (fieldName === 'name') {
      const allowedKeys = [
        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Shift',
        'Control', 'Alt', 'Home', 'End', ' '
      ];

      // Check if the key is a letter or one of the allowed keys
      const isLetter = /^[A-Za-z]$/.test(event.key);
      const isAllowedKey = allowedKeys.includes(event.key);

      // If not a letter or allowed key, prevent the input
      if (!isLetter && !isAllowedKey) {
        event.preventDefault();
      }
    }
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent default form submission
      if (inputBoxAction === "mobileNumberBox") {
        handleSendOtp();
      } else {
        handleVerifyOtp();
      }
    }
  };

  // Handle mobile or OTP popup close icon
  const handleClosePopup = (popupName) => {
    if (popupName === "mobileNoPopup") {
      setShowMobilePopup(false);
      setMobileNumber("");
    } else {
      setShowMobilePopup(true);
      // setShowOtpPopup(false);
      stopTimer();
    }
  };

  return (
    <>
      {/*customTriggerElement ? (
        <div onClick={handleEnquiryClick}>
          {customTriggerElement}
        </div>
      ) :
        < CtaBtn
          customClass={`border-black min-h-11 `}
          name={"Enquiry Now"}
          link='#'
          callFunction={handleEnquiryClick}
        />
      */}
      {showOtpPopup && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-[1000]">
          <div className="relative bg-primary text-center mx-5 p-5 max-w-sm w-full md:max-w-mdd">
            <button
              className="close-popup-icon absolute -top-6 -right-3 text-3xl focus:outline-none"
              onClick={() => handleClosePopup("otpPopup")}
            >
              &times;
            </button>
            <h2 className="text-white">Enter OTP</h2>
            <TextBox
              customClass="border-[rgba(0,0,0,0.5)] border p-3 mt-3"
              attributes={{ name: "otp", placeholder: "Enter OTP" }}
              errorMessage={otpError}
              value={otp}
              isTouched={true}
              isValid={otp.length > 0}
              shouldValidate={true}
              valueChangeHandler={(e) => setOtp(e.target.value)}
              inputBlurHandler={() => { }}
              onKeyDown={(e) => handleKeyDown(e, "otpBox")}
            />
            {otpVerifyLoader ? (
              <div className="spinner mx-auto mt-2"></div> // Show spinner when otpVerifyLoader
            ) : (
              <FormBtn
                customClass="mt-5 mx-auto"
                name={"Verify OTP"}
                onClick={handleVerifyOtp}
              />
            )}
            <div className="text-white mt-2">
              Time Remaining: {formatTime(timer)}
            </div>
            {popupMessage && (
              <p className={`popup-message ${popupType}`}>{popupMessage}</p>
            )}
          </div>
        </div>
      )}

      {clicked && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-[1000]">
          <div className="relative bg-[#363435] text-center mx-5 p-8 w-full max-w-[600px] lg:max-w-[43vw] z-[99]">
            <button
              className="close-popup-icon absolute top-[0px] right-[5px] !text-[30px] !text-[#fff] leading-[1] focus:outline-none !cursor-pointer"
              onClick={handleCloseBlogPopup}
            >
              &times;
            </button>
            <form>
              <div className="md:grid gap-4 lg:gap-[1vw] *:mb-4 md:*:mb-0">

                <div>
                  <TextBox
                    customClass={"border border-[rgba(0,0,0,0.5)] p-4 lg:p-[0.5vw] w-full text-[16px] lg:text-[1.2vw] text-black"}
                    attributes={formElement.name.attributes}
                    errorMessage={formElement.name.errorMessage}
                    value={formElement.name.value}
                    isTouched={formElement.name.isTouched}
                    isValid={!formElement.name.isValid}
                    shouldValidate={formElement.name.validation}
                    valueChangeHandler={inputChangedHandler}
                    inputBlurHandler={inputBlurHandler}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                <div>
                  <TextBox
                    customClass={"border border-[rgba(0,0,0,0.5)] p-4 lg:p-[0.5vw] w-full text-[16px] lg:text-[1.2vw] text-black"}
                    attributes={formElement.email.attributes}
                    errorMessage={formElement.email.errorMessage}
                    value={formElement.email.value}
                    isTouched={formElement.email.isTouched}
                    isValid={!formElement.email.isValid}
                    shouldValidate={formElement.email.validation}
                    valueChangeHandler={inputChangedHandler}
                    inputBlurHandler={inputBlurHandler}
                    onKeyDown={handleKeyDown}
                  />
                </div>

                <div>
                  <TextBox
                    customClass={"border border-[rgba(0,0,0,0.5)] p-4 lg:p-[0.5vw] w-full text-[16px] lg:text-[1.2vw] text-black"}
                    attributes={formElement.contact.attributes}
                    errorMessage={formElement.contact.errorMessage}
                    value={formElement.contact.value}
                    isTouched={formElement.contact.isTouched}
                    isValid={!formElement.contact.isValid}
                    shouldValidate={formElement.contact.validation}
                    valueChangeHandler={inputChangedHandler}
                    inputBlurHandler={inputBlurHandler}
                    onKeyDown={handleKeyDown}
                    type="number"
                  />
                </div>
                <div>
                  <TextBox
                    customClass={"border border-[rgba(0,0,0,0.5)] p-4 lg:p-[0.5vw] w-full text-[16px] lg:text-[1.2vw] text-black"}
                    attributes={formElement.pincode.attributes}
                    errorMessage={formElement.pincode.errorMessage}
                    value={formElement.pincode.value}
                    isTouched={formElement.pincode.isTouched}
                    isValid={!formElement.pincode.isValid}
                    shouldValidate={formElement.pincode.validation}
                    valueChangeHandler={inputChangedHandler}
                    inputBlurHandler={inputBlurHandler}
                    onKeyDown={handleKeyDown}
                    type='number'
                  />
                </div>
              </div>
              <div className="md:grid md:grid-cols-2 pt-5 gap-4 lg:gap-[1.5vw] *:mb-4 md:*:mb-0">

                {formSubmitErrorMessage && <div className="form-submit-error-message mb-3">{formSubmitErrorMessage}</div>}

                <div className="flex flex-wrap items-center justify-between col-span-2">

                  <div className="flex justify-between w-full">

                    {formSubmitLoader ? (
                      <div className="spinner m-auto"></div> // Show spinner when formSubmitLoader
                    ) : (
                      <FormBtn name={"Submit"} onClick={formSubmitHandler} />
                    )}
                    {otpSent && (
                      sendOtpLoader ? (
                        <div className="spinner m-auto"></div> // Show spinner when sendOtpLoader
                      ) : (
                        <FormBtn name={"Send OTP"} onClick={handleSendOtp} />
                      )
                    )}
                  </div>
                </div>
              </div>
            </form>

            {/* <LoginForm handleClosePopup={handleClosePopup} /> */}
          </div>
        </div>
      )}
    </>


  );
};

export default BlogEnquiry;
